import React, { useState , useEffect, useCallback, useRef} from "react";
import "./Popup.css";

const Popup = ({
  onSubmit,
  mode = "create", // Par défaut, on est en mode "create"
  onCancel,
  partsList = [], // Default to empty array
  damagesList = [], // Default to empty array
  suggestionsList = [], // Default to empty array
  initialData = {}, // Add initial data for "edit" mode
  onDelete, // Add delete function
  vin = null, // VIN du véhicule pour pricing API externe
  category = null, // Catégorie du véhicule pour pricing API externe

}) => {
  const [partName, setPartName] = useState(partsList[0] || ""); // Handle empty lists
  const [damageType, setDamageType] = useState(damagesList[0] || ""); // Handle empty lists
  const [damageProb, setDamageProb] = useState(100); // Default to 100%
  const [price, setPrice] = useState(0); // Default to 0
  const [suggestion, setSuggestion] = useState(suggestionsList[0] || ""); // Handle empty lists
  const [moPeint, setMoPeint] = useState(0); // MO peinture per part
  const [ingPeint, setIngPeint] = useState(0); // ING peinture per part
  const [partSearch, setPartSearch] = useState("");
  const [partError, setPartError] = useState(false);
  const [priceLoading, setPriceLoading] = useState(false);
  const fetchTimerRef = useRef(null);

  const userChangedRef = useRef(false); // true seulement si l'user a modifié un champ manuellement

  useEffect(() => {
    //console.log("Popup Initial Data:", initialData); // Log initial data for debugging
    // Sync initial data if provided
    if (initialData) {
      setPartName(initialData.partName || "");
      setDamageType(initialData.damageType || "");
      setDamageProb(initialData.damageProb || 100);
      setPrice(initialData.price || 0);
      setSuggestion(initialData.suggestion || "");
      setMoPeint(initialData.moPeint || 0);
      setIngPeint(initialData.ingPeint || 0);
    }
    setPartSearch("");
    userChangedRef.current = false; // reset à chaque ouverture / changement d'initialData
  }, [initialData]);

  // ✅ Auto-fetch price when part_name + damage_type + suggestion are all set
  const fetchEstimatedPrice = useCallback(async (pName, dType, sugg) => {
    if (!pName || !dType || !sugg) return;
    // Only fetch if part is valid
    if (!partsList.includes(pName)) return;

    setPriceLoading(true);
    try {
      const params = new URLSearchParams({
        part_name: pName,
        damage_type: dType,
        suggestion: sugg,
      });
      if (vin) params.append("vin", vin);
      if (category) params.append("category", category);
      const res = await fetch(`/api/pricing/estimate?${params.toString()}`);
      if (res.ok) {
        const data = await res.json();
        if (data.price !== undefined) {
          setPrice(data.price);
          setMoPeint(data.mo_peint || 0);
          setIngPeint(data.ing_peint || 0);
          console.log(`[Pricing] Auto-estimated: ${pName} / ${dType} / ${sugg} → ${data.price} MAD (mo=${data.mo_peint}, ing=${data.ing_peint})`);
        }
      }
    } catch (err) {
      console.warn("[Pricing] Erreur estimation prix:", err);
    } finally {
      setPriceLoading(false);
    }
  }, [partsList, vin]);

  // Debounced price fetch — uniquement si l'user a changé un champ (ou en mode create)
  useEffect(() => {
    if (mode === "edit" && !userChangedRef.current) return; // En edit, ne pas recalculer au chargement
    if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current);
    fetchTimerRef.current = setTimeout(() => {
      fetchEstimatedPrice(partName, damageType, suggestion);
    }, 300);
    return () => clearTimeout(fetchTimerRef.current);
  }, [partName, damageType, suggestion, fetchEstimatedPrice]);

  //console.log("Popup Mode:", mode);  Log the mode (create/edit) to ensure the correct state


  const handleSubmit = () => {
    if (partName && damageType && price >= 0 && suggestion) {
      const submitData = {
        partName,
        damageType,
        damageProb,
        price,
        suggestion,
        moPeint,
        ingPeint,
      };
      console.log("[Popup] handleSubmit - data being sent to KonvaDrawing:", submitData);
      if (!partsList.includes(partName)) {
        alert("Veuillez sélectionner une pièce valide depuis la liste.");
        return;
      }
      onSubmit(submitData);
      onCancel(); // Close the popup
    } else {
      alert("Veuillez remplir tous les champs obligatoires !");
      console.log("Popup validation failed. Missing fields:", {
        partName,
        damageType,
        price,
        suggestion,
      });
    }
  };
  

  return (
    <div className="popup-overlay">
  <div className="popup-container">
    <h3>{mode === "create" ? "Ajouter un nouveau dommage" : "Modifier le dommage"}</h3>

    {/* Formulaire */}
    <div className="popup-field">
      <label>Nom de la pièce:</label>

      <input
          type="text"
          value={partName || ""}
          placeholder="Tapez pour rechercher..."
          onChange={(e) => {
            const value = e.target.value;
            userChangedRef.current = true;
            setPartName(value);
            setPartSearch(value);
            console.log("Part Name Input Changed:", value); // Log input changes

            // ❌ invalide tant que ce n’est pas une vraie pièce
            setPartError(!partsList.includes(value));
          }}
          onBlur={() => {
            // 🔒 sécurité finale au blur
            if (!partsList.includes(partName)) {
              setPartError(true);
            }
          }}
        />
      {partSearch && (
          <ul className="popup-autocomplete-list">
            {partsList
              .filter((p) => p.toLowerCase().startsWith(partSearch.toLowerCase()))
              .slice(0, 8)
              .map((p) => (
                <li
                  key={p}
                  className="popup-autocomplete-item"
                  onClick={() => {
                    userChangedRef.current = true;
                    setPartName(p);
                    setPartSearch("");
                    setPartError(false); // ✅ valeur validée
                  }}
                >
                  {p.replace(/_/g, " ")}
                </li>
              ))}
          </ul>
        )}
    </div>
    {partError && (
        <small className="popup-error">
          Veuillez sélectionner une pièce existante dans la liste.
        </small>
      )}



    <div className="popup-field">
      <label>Type de dommage:</label>
      <select
        value={damageType || ""}
        onChange={(e) => { userChangedRef.current = true; setDamageType(e.target.value); }}
      >
        <option value="" disabled>
          Sélectionnez un type de dommage
        </option>
        {damagesList.map((damage) => (
          <option key={damage} value={damage}>
            {damage}
          </option>
        ))}
      </select>
    </div>
{/* Formulaire 
    <div className="popup-field">
      <label>Probabilité:</label>
      <input type="number" value={damageProb} readOnly />
    </div>*/}

    <div className="popup-field">
      <label>Prix:{priceLoading && <span style={{ marginLeft: 8, fontSize: 12, color: '#888' }}>⏳ calcul...</span>}</label>
      <input
        type="number"
        value={price}
        onChange={(e) => setPrice(Number(e.target.value))}
      />
    </div>

    <div className="popup-field">
      <label>Suggestion:</label>
      <select
        value={suggestion || ""}
        onChange={(e) => { userChangedRef.current = true; setSuggestion(e.target.value); }}
      >
        <option value="" disabled>
          Sélectionnez une suggestion
        </option>
        {suggestionsList.map((suggestion) => (
          <option key={suggestion} value={suggestion}>
            {suggestion}
          </option>
        ))}
      </select>
    </div>

    {/* Boutons dynamiques */}
    <div className="popup-buttons">
      <button onClick={handleSubmit} className="save-button">
        {mode === "create" ? "Enregistrer" : "Modifier"}
      </button>
      {mode === "edit" && (
        <button onClick={onDelete} className="delete-button">
          Supprimer
        </button>
      )}
      <button onClick={onCancel} className="cancel-button">
        Annuler
      </button>
    </div>
  </div>
</div>
  );
};

export default Popup;